import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePageTitle } from "@/hooks/usePageTitle";
import { LayoutGrid, List, MapPin, Building2, Calendar } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import PublicLanguageSelector from "@/components/PublicLanguageSelector";


interface PublicJob {
  job_id: string;
  title: string;
  org_id: string;
  org_name: string;
  location: string;
  created_at: string;
  jobdesc_public: string;
}

export default function PublicJobsListPage() {
  const { t } = useTranslation();
  const { orgId } = useParams<{ orgId: string }>();
  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGridView, setIsGridView] = useState(true);
  const [orgName, setOrgName] = useState<string>("");
  const [customStyle, setCustomStyle] = useState<any>(null);
	usePageTitle(orgName ? `${orgName} - Job Openings` : "Job Openings");
  useEffect(() => {
    async function fetchJobs() {
      setLoading(true);
      const { data, error } = await supabase
        .from("jobs_public")
        .select("job_id, title, org_id, org_name, location, created_at, jobdesc_public")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (data && data.length > 0) {
        setJobs(data);
        // Get org name from the first job
        setOrgName(data[0].org_name);
      }
      setLoading(false);
    }
    if (orgId) fetchJobs();
  }, [orgId]);

  useEffect(() => {
    async function fetchCustomStyle() {
      if (!orgId) return;
      const { data } = await supabase
        .from("custom_publicstyle")
        .select("customstyle")
        .eq("organization_id", orgId)
        .single();
      if (data && data.customstyle) setCustomStyle(data.customstyle);
    }
    fetchCustomStyle();
  }, [orgId]);

  function truncateDescription(description: string, maxLength: number = 150) {
    const strippedText = description.replace(/<[^>]*>/g, '');
    if (strippedText.length <= maxLength) return strippedText;
    return strippedText.substring(0, maxLength) + "...";
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">{t("jobs.public.jobsList.loading")}</p>
      </div>
    </div>
  );

  // Helper to get color or fallback
  const mainColor = customStyle?.mainColor || '#5a7ebf'; // digger-blue
  const bgColor = customStyle?.bgColor || 'hsl(210 40% 98%)'; // app background
  const textColor = customStyle?.textColor || 'hsl(222 47% 11%)'; // app foreground
  const pageStyle = customStyle?.pageStyle || 'square';
  const borderRadius = pageStyle === 'square' ? '0' : pageStyle === 'rounded' ? '1rem' : '0.25rem';

  return (
    <>
      <div
        className="min-h-screen py-12 px-4"
        style={{
          background: bgColor,
          color: textColor,
        }}
      >
        {/* Language Selector */}
        <div className="w-full max-w-7xl mx-auto flex justify-end mb-4 px-4">
          <PublicLanguageSelector />
        </div>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 px-4">
            {customStyle?.logoUrl && (
              <img
                src={customStyle.logoUrl}
                alt="Organization Logo"
                className="mx-auto mb-6"
                style={{ maxHeight: 80, width: 'auto', maxWidth: '100%' }}
              />
            )}
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: textColor }}
            >
              {customStyle?.title || `${orgName} Job Openings`}
            </h1>
            <div
              className="text-lg mb-8"
              style={{ color: textColor }}
            >
              {customStyle?.headline || "Discover your next career opportunity"}
            </div>
          </div>
          {/* View Toggle */}
          <div className="flex justify-end mb-8 px-4">
            <div className="flex items-center space-x-2 bg-white p-1 shadow-md border" style={{ borderRadius: pageStyle === 'square' ? '0' : '9999px' }}>
                                              <button
                  onClick={() => setIsGridView(false)}
                  className={`px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${!isGridView ? '' : 'hover:bg-gray-100'}`}
                  style={{
                    borderRadius: pageStyle === 'square' ? '0' : '9999px',
                    background: !isGridView ? mainColor : undefined,
                    color: !isGridView ? '#fff' : mainColor,
                    border: `1px solid ${mainColor}`,
                  }}
              >
                <List className="h-5 w-5" style={{ color: !isGridView ? '#fff' : mainColor }} />
                {t("jobs.public.jobsList.list")}
              </button>
              <button
                onClick={() => setIsGridView(true)}
                className={`px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${isGridView ? '' : 'hover:bg-gray-100'}`}
                style={{
                  borderRadius: pageStyle === 'square' ? '0' : '9999px',
                  background: isGridView ? mainColor : undefined,
                  color: isGridView ? '#fff' : mainColor,
                  border: `1px solid ${mainColor}`,
                }}
              >
                <LayoutGrid className="h-5 w-5" style={{ color: isGridView ? '#fff' : mainColor }} />
                {t("jobs.public.jobsList.grid")}
              </button>
            </div>
          </div>
          {/* Job Cards */}
          {jobs.length === 0 ? (
            <div className="text-center py-20 bg-white/80 backdrop-blur-lg shadow-sm border border-gray-200" style={{ borderRadius }}>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">{t("jobs.public.jobsList.noOpenPositions")}</h2>
              <p className="text-gray-500">{t("jobs.public.jobsList.checkBackLater", "Please check back later for new openings.")}</p>
            </div>
          ) : isGridView ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {jobs.map((job) => (
                <Link key={job.job_id} to={`/jobs/${job.job_id}/public`} className="bg-white/80 backdrop-blur-lg shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 flex flex-col overflow-hidden cursor-pointer" style={{ borderRadius }}>
                  <div className="p-6 flex-grow flex flex-col">
                    <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                      {job.title}
                    </h2>
                    <div className="space-y-3 mb-4 text-sm text-gray-600">
                      {job.location && (
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          {job.location}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {t("jobs.public.jobsList.postedOn", { date: format(new Date(job.created_at), 'MMM d, yyyy') })}
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-3 flex-grow">
                      {truncateDescription(job.jobdesc_public)}
                    </p>
                  </div>
                  <div className="p-6 bg-gray-50 border-t mt-auto">
                    <Link
                      to={`/jobs/${job.job_id}/public`}
                      className="font-semibold py-2.5 px-4 text-sm w-full text-center block transition-colors"
                      style={{
                        background: mainColor,
                        color: '#fff',
                        borderRadius: pageStyle === 'square' ? '0' : '0.5rem', // 0.5rem = rounded-lg
                      }}
                    >
                      {t("jobs.public.jobsList.viewDetails", "View Details")}
                    </Link>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Link key={job.job_id} to={`/jobs/${job.job_id}/public`} className="bg-white/80 backdrop-blur-lg shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 flex flex-col md:flex-row items-center p-6 cursor-pointer" style={{ borderRadius }}>
                  <div className="flex-1 mb-4 md:mb-0 md:mr-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                      {job.title}
                    </h2>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                      {job.location && (
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          {job.location}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {t("jobs.public.jobsList.postedOn", { date: format(new Date(job.created_at), 'MMM d, yyyy') })}
                      </div>
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-shrink-0">
                    <span
                      className="font-semibold py-2.5 px-6 text-sm w-full text-center block transition-colors"
                      style={{
                        background: mainColor,
                        color: '#fff',
                        borderRadius: pageStyle === 'square' ? '0' : '0.5rem',
                      }}
                    >
                      {t("jobs.public.jobsList.viewDetails", "View Details")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        {/* Footer - moved inside main container to prevent iframe scrollbar issues */}
        <div className="w-full max-w-7xl mx-auto mt-8">
          <footer className="border-t border-gray-200 py-4 flex flex-col items-center text-xs text-gray-500">
            <span className="mb-1">Powered by</span>
            <a href="https://digger-ai.be" target="_blank" rel="noopener noreferrer" className="flex items-center gap-0 mt-1">
              <div className="w-14 h-14 rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/img/NewDiggerIcon.png" alt="Digger" className="w-full h-full object-contain" />
              </div>
              <span className="text-3xl font-black tracking-tight text-gray-700">digger<span className="inline-block w-2 h-2 rounded-full bg-[#5a7ebf] ml-1 align-middle" /></span>
            </a>
          </footer>
        </div>
      </div>
    </>
  );
} 